"use client";

import { IconInfoCircle } from "@tabler/icons-react";
import { useFormatter, useTranslations } from "next-intl";
import { InfoTooltip } from "@/components/common/info-tooltip";
import { Rating } from "@/components/common/rating";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { ListingItemImage } from "@/components/listing/listing-item-image";
import { Button } from "@/components/ui/button";
import { DietListItemOrderButton } from "@/features/diets/components/diet-list-item/diet-list-item-order-button";
import { DietListItemPrice } from "@/features/diets/components/diet-list-item/diet-list-item-price";
import { DietMenuModal } from "@/features/diets/components/diet-menu-modal/diet-menu-modal";
import type { DietCatalog } from "@/features/diets/types";
import {
  formatCateringName,
  formatDietName,
} from "@/lib/helpers/format-brand-name";

export const DietRankingCard = ({
  diet,
  place,
  score,
  scoreTooltip,
}: {
  diet: DietCatalog;
  place: number;
  score: number;
  scoreTooltip: string | null;
}) => {
  const t = useTranslations();
  const format = useFormatter();
  const dietName = formatDietName(diet.name);
  const cateringName = formatCateringName(diet.catering.name);
  const kcal = diet.calorifics.at(0)?.calories.toString();

  return (
    <article className="flex flex-col overflow-hidden rounded-xl">
      <div className="flex items-center justify-between gap-4 bg-background-surface-brand-primary px-4 py-3">
        <p className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded-sm bg-background-fill-secondary font-bold text-base text-text leading-6">
            {place}
          </span>
          <span className="font-medium text-text-inverse text-xl leading-7">
            {t("diets.ranking_place")}
          </span>
        </p>
        <p className="flex shrink-0 items-center gap-1 rounded-full bg-background-fill-secondary px-3 py-1.5 font-medium text-text text-xs leading-[18px]">
          {t("diets.ranking_score", {
            score: format.number(score, { maximumFractionDigits: 2 }),
          })}
          {scoreTooltip && (
            <InfoTooltip asChild content={scoreTooltip}>
              <button
                aria-label={t("diets.ranking_score_info")}
                className="flex shrink-0"
                type="button"
              >
                <IconInfoCircle className="size-4" stroke={1.5} />
              </button>
            </InfoTooltip>
          )}
        </p>
      </div>

      <div className="flex flex-1 flex-col items-stretch rounded-b-xl border-border border-r border-b border-l bg-background-surface-primary md:flex-row">
        <ListingItemImage
          className="aspect-video w-full shrink-0 rounded-none! md:aspect-auto md:w-[138px] md:self-stretch"
          imageClassName="rounded-none!"
          imageUrl={diet.imageUrl}
          name={dietName}
        >
          <ListingItemCateringImage
            className="absolute top-2 left-2"
            imageUrl={diet.catering.logo.url}
            name={cateringName}
          />
        </ListingItemImage>

        <div className="flex flex-1 flex-col gap-3 p-4 md:px-4 md:py-6">
          <div className="flex flex-col md:flex-row md:items-center md:gap-2">
            <div className="flex items-center gap-2 md:contents">
              <p className="min-w-0 flex-1 truncate text-sm text-text-subtle leading-[22px] md:order-3 md:text-xl md:leading-7">
                {cateringName}
              </p>
              <Rating
                className="shrink-0 bg-background-fill-primary md:order-4 md:ml-auto"
                count={diet.rating.count}
                details={false}
                productId={diet.id}
                productName={dietName}
                rating={diet.rating.value}
                size="tag"
              />
            </div>
            <h3 className="truncate font-medium text-text text-xl leading-7 md:order-1">
              {dietName}
            </h3>
            <span className="hidden size-1 shrink-0 rounded-full bg-text-subtle md:order-2 md:block" />
          </div>

          <div className="mt-auto flex flex-col items-start justify-between gap-5 md:mt-0 md:flex-row md:items-center md:gap-4">
            <DietListItemPrice
              discountCodes={diet.discountCodes}
              price={diet.basePrice}
              t={t}
            />
            <div className="grid w-full grid-cols-2 gap-2 md:flex md:w-auto">
              <DietMenuModal
                dietId={diet.id}
                kcal={kcal}
                trigger={
                  <Button
                    className="h-12 min-h-0 w-full rounded-xl border-brand-green2-300 px-5 font-medium text-text md:w-[140px]"
                    variant="outline"
                  >
                    {t("diets.check_menu")}
                  </Button>
                }
              />
              <div className="md:w-[140px]">
                <DietListItemOrderButton
                  configParams={{
                    configuration:
                      diet.searchResultId || diet.configurationId || "",
                  }}
                  diet={diet}
                  kcal={kcal}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
